import { CaretDownIcon } from '@radix-ui/react-icons'
import { ResetIcon } from '@/components/icons/ResetIcon'
import { DrumIcon } from '@/components/icons/DrumIcon'
import { DrumOutlineIcon } from '@/components/icons/DrumOutlineIcon'
import { DrumTransparentIcon } from '@/components/icons/DrumTransparentIcon'
import { DrumTransparentOutlineIcon } from '@/components/icons/DrumTransparentOutlineIcon'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu'
import { ControlButton } from './ControlButton'
import { ExplosionsToolbar } from './ExplosionsButton/ExplosionsButton'
import { CrossSectionButton } from './CrossSectionButton/CrossSectionButton'
import { ToggleStepsButton } from '@/pages/CADPage/components/ControlPanel/ToggleStepsButton'
import { ToggleModelButton } from '@/pages/CADPage/components/ControlPanel/ToggleModelButton'
import { DISTANCE_FACTOR } from '@/pages/CADPage/constants'
import { ISOMETRIC_CONSTS } from '@/lib/workers/constants'

import { useShallow } from 'zustand/react/shallow'
import { useCADQuery } from '@/services/queries/cads'
import { AppearanceButton } from './AppearanceButton'
import { useCADPageStore, SectionState, ExplosionsState } from '@/state/cad'
import { StepHighlighterButton } from '../AssemblyOrderPanel/StepHighlighterButton'

export const Controls = () => {
  const explosions = useCADPageStore((state) => state.explosions)
  const disableSectioning = useCADPageStore((state) => state.disableSectioning)
  const setCadPageState = useCADPageStore(useShallow((state) => state.setState))
  const setWandSelected = useCADPageStore((state) => state.setWandSelected)
  const renderMode = useCADPageStore((state) => state.renderMode)
  const parts = useCADPageStore(useShallow((state) => state.transparentParts))
  const colorMap = useCADPageStore((state) => state.colorMap)

  const { isLoading, data: cadData } = useCADQuery()
  const gltf = cadData.gltf
  const assemblyTree = cadData.rawAssemblyTree

  if (isLoading || !gltf || !assemblyTree) return null

  return (
    <>
      <div>
        <ControlButton
          title="Reset All"
          icon={<ResetIcon className="w-5 h-5" />}
          onClick={() => {
            const { sceneCenter, boundsMax } = useCADPageStore.getState()

            gltf.showExplosionLines(explosions, false)
            gltf.resetExplosions(explosions)
            gltf.unhighlightParts(colorMap)
            gltf.clearDragLines()
            gltf.resetColors()
            gltf.resetTransparency(parts)
            gltf.resetClippingPlanes()

            assemblyTree.nodes.map((node) =>
              gltf.setVisibility(node.instance, true),
            )

            const cameraPosition = boundsMax
              ? [
                  boundsMax * DISTANCE_FACTOR,
                  boundsMax * DISTANCE_FACTOR,
                  boundsMax * DISTANCE_FACTOR,
                ]
              : useCADPageStore.getState().cameraPosition

            setCadPageState({
              target: sceneCenter?.toArray() || [0, 0, 0],
              cameraPosition,
              cameraFov: ISOMETRIC_CONSTS.FOV,
              cameraZoom: ISOMETRIC_CONSTS.ZOOM,
              cameraQuaternion: ISOMETRIC_CONSTS.QUATERNION,
              cameraUp: [0, 1, 0],
              isDragging: false,
              selectedParts: [],
              loadedExplosion: '',
              explosions: {},
              hiddenParts: [],
              colorMap: {},
              transparentParts: [],
              highlightedPartUUID: null,
              isEditModeActive: false,
              clippingPlanes: [],
              sectionState: SectionState.RESET,
              explosionsState: ExplosionsState.DISABLED,
              operationStep: null,
              highlightColor: null,
              highlightedParts: [],
              highlighting: false,
              showFullModel: false,
              hidePreviousOperations: false,
            })
          }}
        />
      </div>
      <StepHighlighterButton gltf={gltf} />
      <AppearanceButton />
      <ExplosionsToolbar />
      <CrossSectionButton />
      <div>
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger>
            <ControlButton
              asChild
              icon={
                <div className="flex flex-row items-center">
                  <DrumIcon className="w-5 h-5" />
                  <CaretDownIcon />
                </div>
              }
              onClick={(e) => {
                e.stopPropagation()
                setWandSelected(null)
                setCadPageState({
                  selectedParts: [],
                  explosionsState: ExplosionsState.DISABLED,
                })
                disableSectioning()
              }}
              title="Display"
            />
          </DropdownMenuTrigger>
          <DropdownMenuContent className="flex flex-row">
            <DropdownMenuItem>
              <ControlButton
                isActive={renderMode === 'full-color'}
                icon={<DrumTransparentIcon className="w-5 h-5" />}
                title="Color"
                onClick={() => {
                  setCadPageState({ renderMode: 'full-color' })
                }}
              />
            </DropdownMenuItem>
            <DropdownMenuItem>
              <ControlButton
                isActive={renderMode === 'outline'}
                icon={<DrumTransparentOutlineIcon className="w-5 h-5" />}
                title="Color with Outline"
                onClick={() => {
                  setCadPageState({ renderMode: 'outline' })
                }}
              />
            </DropdownMenuItem>
            <DropdownMenuItem>
              <ControlButton
                isActive={renderMode === 'black-and-white-wireframe'}
                icon={<DrumOutlineIcon className="w-5 h-5" />}
                title="Outline"
                onClick={() => {
                  setCadPageState({
                    renderMode: 'black-and-white-wireframe',
                  })
                }}
              />
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <ToggleStepsButton />
      <ToggleModelButton />
    </>
  )
}
